import { createRouter } from "@tanstack/solid-router";
import { NotFoundRoute } from "@/components/not-found-route";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { pluginRoutes } from "@/composition";
import { indexRoute } from "@/routes/_app/index";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";
import { sandboxModalRoute } from "@/routes/_app/modal";
import { chronosRoute } from "@/routes/_app/chronos";
import { chronosIndexRoute } from "@/routes/_app/chronos/index";
import { chronosTimelineRoute } from "@/routes/_app/chronos/timeline";
import { chronosDeepWorkRoute } from "@/routes/_app/chronos/deep-work";
import { chronosAnalyticsRoute } from "@/routes/_app/chronos/analytics";
import { chronosTeamRoute } from "@/routes/_app/chronos/team";
import { chronosProjectsRoute } from "@/routes/_app/chronos/projects";
import { chronosTimesheetRoute } from "@/routes/_app/chronos/timesheet";
import { chronosSettingsRoute } from "@/routes/_app/chronos/settings";
import { chronosTrashRoute } from "@/routes/_app/chronos/trash";
import { chronosActivityRoute } from "@/routes/_app/chronos/activity";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    indexRoute, // path: "/" — Dashboard (redirects to /chronos)
    ...pluginRoutes,
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    sandboxModalRoute,
    debugRoute,
    chronosRoute.addChildren([
      chronosIndexRoute,
      chronosTimelineRoute,
      chronosDeepWorkRoute,
      chronosAnalyticsRoute,
      chronosTeamRoute,
      chronosProjectsRoute,
      chronosTimesheetRoute,
      chronosSettingsRoute,
      chronosTrashRoute,
      chronosActivityRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  // Any unknown URL shows a clear "not found" state (with
  // a "go to home" action) instead of a blank screen.
  defaultNotFoundComponent: NotFoundRoute,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). The previous "viewport" setting eagerly fired `beforeLoad` and
  // PowerSync `resolveTables` EXPLAIN queries for ~20 sidebar routes the
  // moment the dashboard mounted. Combined with PowerSync's initial-sync
  // batches (each one re-invalidating subscribed queries), this saturated
  // the main thread for several seconds after login. Hover-only is the
  // standard tradeoff: a tiny first-hover latency in exchange for a calm
  // post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
